// Dagens vagt: én bane, ét frø og én modifikator pr. dag, ens for alle uden en server. Datoen er
// frøet, så to spillere, der spiller samme dag, får samme vejr og kan sammenligne deres gitter.
// Resultatet deles som tekst: et felt pr. år (grøn: ingen mørklagte byer, gul: én, rød: flere,
// sort: året nåede man ikke) og stjernerne.

import type { DailyMod, DifficultyId } from '../sim/types';
import { LEVELS } from './levels';

export interface DailyPlan {
  /** Datoen som YYYY-MM-DD (lokal tid). */
  key: string;
  /** Vagtens nummer: dag 1 er 1. oktober 2026. */
  number: number;
  level: string;
  difficulty: DifficultyId;
  seed: number;
  mod: DailyMod;
}

export const DAILY_MODS: Record<DailyMod, { name: string; text: string }> = {
  tight: { name: 'Stram kasse', text: '30 % færre kroner i kassen fra start.' },
  stormy: { name: 'Blæsende dag', text: 'Halvanden gang så meget vejr.' },
  still: { name: 'Stille dag', text: 'Vindmøllerne giver 20 % mindre hele banen.' },
};

const MODS: DailyMod[] = ['tight', 'stormy', 'still'];
const DAY0 = Date.UTC(2026, 9, 1);

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

export function dailyKey(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** FNV-1a over datoen: et stabilt tal, ens i alle browsere. */
function hash(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

export function dailyPlan(d: Date): DailyPlan {
  const key = dailyKey(d);
  const h = hash(`fs-dagens-vagt:${key}`);
  const day = Math.round((Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) - DAY0) / 86_400_000);
  const level = LEVELS[h % LEVELS.length] ?? LEVELS[0];
  return {
    key,
    number: Math.max(1, day + 1),
    level: level?.id ?? '01-foerste-vind',
    difficulty: 'vagthavende',
    seed: (h % 999_983) + 1,
    mod: MODS[Math.floor(h / 7) % MODS.length] ?? 'tight',
  };
}

/** Et felt pr. år: antal mørklagte byer i året, eller null hvis året aldrig blev spillet. */
export function yearSquare(blackouts: number | null): string {
  if (blackouts === null) return '⬛';
  if (blackouts === 0) return '\u{1F7E9}';
  if (blackouts === 1) return '\u{1F7E8}';
  return '\u{1F7E5}';
}

/** Stjernerne som tekst: ★★☆, eller Tabt. */
export function starText(stars: number): string {
  return stars > 0 ? '★'.repeat(stars) + '☆'.repeat(3 - stars) : 'Tabt';
}

/** Dagen før (lokal kalender, også hen over sommertid og årsskifte). */
export function prevKey(key: string): string {
  const [y, m, d] = key.split('-').map(Number);
  return dailyKey(new Date(y ?? 2026, (m ?? 1) - 1, (d ?? 1) - 1, 12));
}

/** Vagter i træk: dage i streg med en spillet Dagens vagt (vundet eller ej). */
export interface DailyStreak {
  last: string;
  n: number;
  best: number;
}

/** Stimen efter at have spillet dagen `key`. Samme dag igen tæller ikke dobbelt. */
export function nextStreak(prev: DailyStreak | null | undefined, key: string): DailyStreak {
  if (prev && prev.last === key) return prev;
  const n = prev && prev.last === prevKey(key) ? prev.n + 1 : 1;
  return { last: key, n, best: Math.max(n, prev?.best ?? 0) };
}

/** Stimen, som den står i dag: lever stadig, hvis sidste vagt var i dag eller i går. */
export function streakNow(s: DailyStreak | null | undefined, today: string): number {
  if (!s) return 0;
  return s.last === today || s.last === prevKey(today) ? s.n : 0;
}

export function dailyShareText(
  plan: DailyPlan,
  title: string,
  years: number,
  marks: number[],
  stars: number,
  url: string,
  extra?: { par?: { stars: number } | null; streak?: number },
): string {
  const grid = Array.from({ length: years }, (_, i) => yearSquare(marks[i] ?? null)).join('');
  const bits: string[] = [];
  if (extra?.par) bits.push(`Par ${starText(extra.par.stars)}`);
  if (extra?.streak && extra.streak > 1) bits.push(`${extra.streak} vagter i træk`);
  const lines = [`Dagens vagt #${plan.number} · ${DAILY_MODS[plan.mod].name}`, title, `${grid} ${starText(stars)}`];
  if (bits.length) lines.push(bits.join(' · '));
  lines.push(url);
  return lines.join('\n');
}
